/**
 * 平台帳號（User）：API 可能掛在 `userInfo` 或 populate 後的 `userId`。
 * getCustomerBookings 等回傳多為 `userId`，僅讀 userInfo 會漏掉 LINE 頭像／lineId。
 */
export function resolvePlatformUser(customer) {
  if (!customer) return null
  const uinfo = customer.userInfo
  if (uinfo && typeof uinfo === 'object') {
    if (uinfo._id != null || uinfo.name != null || uinfo.phone != null) {
      return uinfo
    }
  }
  const uid = customer.userId
  if (uid && typeof uid === 'object') {
    if (uid._id != null || uid.name != null || uid.phone != null) {
      return uid
    }
  }
  return null
}

/**
 * 店家端顧客顯示：優先 businessSpecificData / businessRelations，再退回平台欄位。
 * 與 Customers、CustomerDetail、預約表單等共用，避免 LINE 客戶名稱來源不一致。
 *
 * @param customer    顧客文件
 * @param businessId  （可選但強烈建議）當前商家 ID。多店客戶時必填，否則
 *                    businessRelations[0] 可能是別家店的資料造成等級顯示錯誤。
 */
export function getCustomerDisplayData(customer, businessId) {
  if (!customer) {
    return {
      name: '未知',
      phone: '未提供',
      email: '未知',
      gender: undefined,
      birthdate: undefined,
      region: '',
      notes: undefined,
      tags: [],
      membershipLevel: undefined,
      points: 0,
      totalSpent: 0,
      blacklist: false,
      platformName: undefined,
      platformPhone: undefined,
      platformEmail: undefined,
      hasPlatformAccount: false,
      hasOfficialLineAccountPaired: false,
      officialLineAccountPairedAt: null
    }
  }

  // 多店客戶：依 businessId 找對應的 relation；找不到才退回第一筆
  const relations = Array.isArray(customer.businessRelations)
    ? customer.businessRelations
    : []
  let businessRelation = null
  if (businessId) {
    const target = String(businessId)
    businessRelation = relations.find(
      (r) => r && r.businessId && String(r.businessId) === target
    )
  }
  if (!businessRelation) businessRelation = relations[0]
  const businessData =
    businessRelation?.businessSpecificData ||
    customer.businessSpecificData ||
    {}
  const u = resolvePlatformUser(customer)

  return {
    name: businessData.name || customer.name || u?.name || '未知',
    phone: businessData.phone || u?.phone || customer.phone || '未提供',
    email: businessData.email || customer.email || u?.email || '未知',
    gender: businessData.gender || customer.gender || u?.gender,
    birthdate:
      businessData.birthdate || customer.birthdate || u?.birthdate,
    notes: businessData.notes || customer.notes,
    // 地址：只存在 businessSpecificData，平台端沒有這個欄位
    region: businessData.region || '',
    tags: businessData.tags || customer.tags || [],
    membershipLevel:
      businessData.membershipLevel ||
      customer.membershipLevel ||
      businessData.membershipTier ||
      customer.membershipTier,
    points: businessData.points || customer.points || 0,
    totalSpent: businessData.totalSpent || customer.totalSpent || 0,
    blacklist: businessData.blacklist || customer.blacklist || false,
    platformName: customer.name || u?.name,
    platformPhone: u?.phone || customer.phone,
    platformEmail: customer.email || u?.email,
    hasPlatformAccount: !!(u?._id || customer.userId || customer.userInfo),
    hasOfficialLineAccountPaired: customer.lineOfficialAccountPaired === true,
    officialLineAccountPairedAt: customer.lineOfficialAccountPairedAt || null
  }
}

/**
 * 若為 LINE 用戶且 LINE 顯示名與店家慣用名不同，回傳「（LINE：…）」供接在名稱後。
 */
export function getLineDisplayNameSuffix(customer, displayData) {
  if (!customer || !displayData) return ''
  const u = resolvePlatformUser(customer)
  const hasLine =
    customer.isLineUser === true ||
    customer.platformData?.isLineUser === true ||
    u?.isLineUser === true ||
    !!(u?.lineId || u?.isLineConnected)
  if (!hasLine) return ''
  const lineName = (u?.name || '').trim()
  const shown = (displayData.name || '').trim()
  if (!lineName || shown === lineName) return ''
  return `（LINE：${lineName}）`
}
